import React, { useState, useEffect } from 'react';

const AboutMASTIVIBES = () => {
  const [animatedTitle, setAnimatedTitle] = useState('');
  const [currentMilestone, setCurrentMilestone] = useState(0);
  
  const fullTitle = "Redefining Social Connection Through Innovation";
  
  const milestones = [
    {
      year: '2021',
      title: 'Platform Launch',
      description: 'MASTIVIBES was born to create meaningful social connections beyond traditional platforms with custom features.',
      icon: '🚀'
    },
    {
      year: '2022',
      title: 'Community Growth',
      description: 'Reached 100,000 users and introduced group video calls and custom field creation features.',
      icon: '👥'
    },
    {
      year: '2023',
      title: 'Global Expansion',
      description: 'Expanded to 50+ countries with 500,000+ active users and advanced community features.',
      icon: '🌍'
    },
    {
      year: '2024',
      title: 'Innovation Era',
      description: 'Launched AI-powered features, voice rooms, and unlimited customization options for creators.',
      icon: '✨'
    }
  ];

  const teamMembers = [
    {
      name: 'Alex Rivera',
      role: 'Chief Executive Officer',
      expertise: 'Social Technology & Community Building',
      avatar: '👨‍💼',
      gradient: 'from-pink-500 to-purple-500'
    },
    {
      name: 'Samira Khan',
      role: 'Community Director',
      expertise: 'User Experience & Community Engagement',
      avatar: '👩‍🎤',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      name: 'Dr. Marcus Chen',
      role: 'Technical Director',
      expertise: 'Real-time Communication & Video Technology',
      avatar: '👨‍💻',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      name: 'Emily Watson',
      role: 'Product Director',
      expertise: 'Feature Development & User Innovation',
      avatar: '👩‍🎨',
      gradient: 'from-green-500 to-teal-500'
    }
  ];

  const values = [
    {
      icon: '👥',
      title: 'Community First',
      description: 'We believe in building authentic connections and empowering communities to thrive.'
    },
    {
      icon: '🎨',
      title: 'Unlimited Creativity',
      description: 'Every user can customize their experience with unique fields, themes, and features.'
    },
    {
      icon: '⚡',
      title: 'Real-time Connection',
      description: 'Instant communication through video calls, voice rooms, and collaborative tools.'
    },
    {
      icon: '🤝',
      title: 'User Empowerment',
      description: 'Providing tools that let users create, connect, and celebrate their way.'
    }
  ];

  // Social platform floating elements
  const socialFloatingIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-20 left-20', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-32 right-32', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-32 left-32', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-20 right-20', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/2 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' },
    { icon: '✨', size: 'w-11 h-11', position: 'top-1/4 right-1/3', delay: 'delay-3000' },
    { icon: '🎊', size: 'w-9 h-9', position: 'bottom-1/4 left-1/3', delay: 'delay-3500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setAnimatedTitle(fullTitle.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const milestoneInterval = setInterval(() => {
      setCurrentMilestone((prev) => (prev + 1) % milestones.length);
    }, 4000);

    return () => clearInterval(milestoneInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 py-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(25)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.1}s`,
              opacity: 0.6
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${item.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${7 + index}s`
          }}
        >
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Animated Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30 mb-8 shadow-lg">
            <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-md"></span>
            <span className="text-white font-bold">Connecting Communities Since 2021</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
              {animatedTitle}
            </span>
            <span className="animate-pulse text-yellow-300">|</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            MASTIVIBES was created to revolutionize how people connect, create, and celebrate together. 
            We believe social platforms should empower creativity, not limit it.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl transform hover:scale-105 transition-all duration-500 hover:shadow-pink-500/25">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-2xl flex items-center justify-center text-2xl text-white shadow-2xl">
                🎯
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Our Mission</h3>
                <div className="flex space-x-2 mt-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-100 shadow-md"></div>
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-200 shadow-md"></div>
                </div>
              </div>
            </div>
            <p className="text-white/80 text-lg leading-relaxed font-medium">
              To empower individuals and communities with unlimited creative tools for connection, 
              enabling meaningful relationships through customizable social experiences that celebrate uniqueness.
            </p>
          </div>

          <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl transform hover:scale-105 transition-all duration-500 hover:shadow-pink-500/25">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-400 to-purple-500 rounded-2xl flex items-center justify-center text-2xl text-white shadow-2xl">
                🔭
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Our Vision</h3>
                <div className="flex space-x-2 mt-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-100 shadow-md"></div>
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-200 shadow-md"></div>
                </div>
              </div>
            </div>
            <p className="text-white/80 text-lg leading-relaxed font-medium">
              A world where social platforms amplify human connection through creativity, 
              where every person can design their social space and build communities that 
              reflect their authentic selves and shared passions.
            </p>
          </div>
        </div>

        {/* Our Journey - Timeline */}
        <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Our{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                Evolution
              </span>
            </h3>
            <p className="text-white/90 text-lg font-medium">
              From a simple idea to a global social innovation platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <div 
                key={index}
                className={`text-center p-6 bg-white/10 rounded-2xl border-2 border-white/20 transition-all duration-500 transform ${
                  index === currentMilestone ? 'scale-105 bg-white/15 shadow-2xl' : 'scale-100'
                } hover:scale-105 hover:bg-white/15 group shadow-lg`}
              >
                <div className="text-5xl mb-4 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  {milestone.icon}
                </div>
                <div className="text-yellow-300 font-bold text-2xl mb-2">{milestone.year}</div>
                <div className="text-white font-bold text-lg mb-3">{milestone.title}</div>
                <p className="text-white/80 text-sm leading-relaxed font-medium">
                  {milestone.description}
                </p>
                {index === currentMilestone && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-ping shadow-lg"></div>
                )}
                <div className="mt-4 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Innovation Team */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Meet Our{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                Innovation Team
              </span>
            </h3>
            <p className="text-white/90 text-lg max-w-2xl mx-auto font-medium">
              Creative minds dedicated to revolutionizing social connection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <div 
                key={index}
                className="bg-white/15 backdrop-blur-lg rounded-2xl p-6 border-2 border-white/25 hover:bg-white/20 transition-all duration-300 hover:scale-105 group text-center shadow-xl"
              >
                <div className={`w-24 h-24 mx-auto bg-gradient-to-r ${member.gradient} rounded-2xl flex items-center justify-center text-4xl mb-4 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-2xl`}>
                  {member.avatar}
                </div>
                <h4 className="text-white font-bold text-xl mb-2">{member.name}</h4>
                <div className="text-yellow-300 font-semibold mb-2">{member.role}</div>
                <div className="text-white/80 text-sm font-medium">{member.expertise}</div>
                <div className="flex justify-center space-x-2 mt-4">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-100 shadow-md"></div>
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-200 shadow-md"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Our Philosophy */}
        <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Our{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                Core Principles
              </span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 p-6 bg-white/10 rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 group shadow-lg"
              >
                <div className="text-4xl transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300">
                  {value.icon}
                </div>
                <div>
                  <h4 className="text-white font-bold text-xl mb-2 group-hover:text-yellow-300 transition-colors duration-300">
                    {value.title}
                  </h4>
                  <p className="text-white/80 leading-relaxed font-medium">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Impact Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {[
            { 
              number: '500K+', 
              label: 'Active Users', 
              icon: '👥',
              description: 'Global community' 
            },
            { 
              number: '50K+', 
              label: 'Active Groups', 
              icon: '💬',
              description: 'Communities created' 
            },
            { 
              number: '10K+', 
              label: 'Daily Calls', 
              icon: '📹',
              description: 'Video conferences' 
            },
            { 
              number: '24/7', 
              label: 'Live Support', 
              icon: '⚡',
              description: 'Always available' 
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-white/10 backdrop-blur-lg rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 transform hover:-translate-y-2 group shadow-xl"
            >
              <div className="text-4xl mb-4 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                {stat.icon}
              </div>
              <div className="text-4xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                {stat.number}
              </div>
              <div className="text-white font-bold mt-2">
                {stat.label}
              </div>
              <div className="text-white/60 text-sm mt-1">
                {stat.description}
              </div>
              <div className="mt-4 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Community Features Showcase */}
        <div className="bg-gradient-to-r from-yellow-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border-2 border-yellow-400/30 shadow-2xl mb-16">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">
              What Makes{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                MASTIVIBES Unique
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="text-4xl mb-3">🎨</div>
              <h4 className="text-white font-bold text-lg mb-2">Unlimited Custom Fields</h4>
              <p className="text-white/80 text-sm">Create unique profiles and groups with personalized fields</p>
            </div>
            <div className="text-center p-4">
              <div className="text-4xl mb-3">👥</div>
              <h4 className="text-white font-bold text-lg mb-2">Group Video Calls</h4>
              <p className="text-white/80 text-sm">100+ participants with screen sharing and recording</p>
            </div>
            <div className="text-center p-4">
              <div className="text-4xl mb-3">🎵</div>
              <h4 className="text-white font-bold text-lg mb-2">Voice Rooms</h4>
              <p className="text-white/80 text-sm">24/7 audio hangouts with music sharing and effects</p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-yellow-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border-2 border-yellow-400/30 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Create Your Social Space?
            </h3>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto font-medium">
              Join thousands of creators, communities, and connectors who are building amazing experiences on MASTIVIBES.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-xl blur opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl">
                  <span className="mr-3 text-xl">🚀</span>
                  Start Your Free Journey
                  <span className="ml-3 text-xl group-hover:translate-x-2 transition-transform">→</span>
                </div>
              </button>
              <button className="border-3 border-yellow-400/50 text-yellow-300 hover:bg-yellow-400/20 font-bold py-4 px-8 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center backdrop-blur-lg shadow-lg">
                <span className="mr-3 text-xl">📱</span>
                Get Mobile App
                <span className="ml-2 text-xs px-2 py-1 bg-yellow-500/30 rounded-full">NEW</span>
              </button>
            </div>
            <div className="mt-6 text-white/70 text-sm">
              No credit card required • Free forever for basic features
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-20px) rotate(5deg) scale(1.1); 
          }
          66% { 
            transform: translateY(10px) rotate(-3deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default AboutMASTIVIBES;